import React from 'react';
import { Button } from './ui/button';
import { Target, Calendar, Zap, TrendingUp, Check, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const iconComponents = [Target, Calendar, Zap, TrendingUp];

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const steps = t('howItWorks.steps');
  const deliverables = t('howItWorks.deliverables');

  return (
    <section id="how-it-works" className="py-16 bg-slate-50">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0d1442] mb-4">
            {t('howItWorks.title')} <span className="gradient-text">{t('howItWorks.titleHighlight')}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Process Steps */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {Array.isArray(steps) && steps.map((step, index) => {
                const IconComponent = iconComponents[index];
                return (
                  <div key={step} className="relative">
                    <div className="card-hover bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-slate-100 shadow-sm h-full">
                      {/* Step number */}
                      <div className="absolute -top-2 -left-2 sm:-top-3 sm:-left-3 w-6 h-6 sm:w-8 sm:h-8 bg-[#1a237e] rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                        {index + 1}
                      </div>
                      
                      {/* Icon */}
                      <div className="icon-badge w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-4">
                        <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-[#1a237e]" />
                      </div>

                      {/* Content */}
                      <h3 className="text-sm sm:text-lg font-bold text-[#0d1442] mb-1 sm:mb-2">{step.title}</h3>
                      <p className="text-xs sm:text-sm text-slate-600">{step.description}</p>
                    </div>

                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-[#1a237e] to-[#5c6bc0]" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* What You Get Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm h-full">
              <h3 className="text-lg font-bold text-[#0d1442] mb-6">{t('howItWorks.whatYouGet')}</h3>
              <ul className="space-y-4">
                {Array.isArray(deliverables) && deliverables.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="icon-badge p-1.5 rounded-lg mt-0.5">
                      <Check className="w-4 h-4 text-[#1a237e]" />
                    </div>
                    <span className="text-sm text-slate-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
